import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Send, RefreshCw, MessageCircle, AlertTriangle, Phone, X, Edit3 } from 'lucide-react';

const QUICK_PROMPTS = [
  "I can't stop overthinking. What do I do?",
  "Fear is talking me out of something. Help.",
  "I'm stuck comparing myself to others.",
  "My past keeps running my decisions.",
  "How do I handle anxiety in the moment?",
];

// Detect crisis language
const CRISIS_KEYWORDS = ['suicide','kill myself','end my life','want to die','self harm','self-harm','hurt myself','no reason to live'];
const detectCrisis = (txt) => {
  const t = (txt || '').toLowerCase();
  return CRISIS_KEYWORDS.some(k => t.includes(k));
};

export default function Coach() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [coachName, setCoachName] = useState(null);
  const [showNameModal, setShowNameModal] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [crisisAlert, setCrisisAlert] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    const [profileR, histR] = await Promise.all([
      api.get('/coach/profile').then(r => r.data).catch(() => ({})),
      api.get('/coach/history').then(r => r.data).catch(() => []),
    ]);
    if (!profileR.coach_name) {
      setShowNameModal(true);
    } else {
      setCoachName(profileR.coach_name);
    }
    setMessages(histR || []);
    // hide disclaimer if returning user
    if ((histR || []).length > 0) {
      const seen = localStorage.getItem('kc_coach_disclaimer_seen');
      if (seen) setShowDisclaimer(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages]);

  const dismissDisclaimer = () => {
    setShowDisclaimer(false);
    localStorage.setItem('kc_coach_disclaimer_seen', '1');
  };

  const saveName = async () => {
    const name = (nameInput.trim() || 'Mick');
    await api.post('/coach/profile', { coach_name: name });
    setCoachName(name);
    setShowNameModal(false);
    setNameInput('');
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    setInput('');
    setSending(true);
    if (detectCrisis(msg)) setCrisisAlert(true);
    setMessages((m) => [...m, { role: 'user', content: msg, id: 'tmp-' + Date.now() }]);
    try {
      const { data } = await api.post('/coach/chat', { message: msg });
      if (data.coach_name && data.coach_name !== coachName) setCoachName(data.coach_name);
      setMessages((m) => [...m, { role: 'assistant', content: data.reply, id: 'tmp-r-' + Date.now() }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: `${coachName || 'Coach'} is taking a breath. Try again in a moment.`, id: 'err-' + Date.now() }]);
    } finally { setSending(false); }
  };

  const clearHistory = async () => {
    if (!window.confirm('Clear chat history? This cannot be undone.')) return;
    await api.delete('/coach/history');
    setMessages([]);
    setCrisisAlert(false);
  };

  return (
    <div className="kc-container py-6 md:py-10 max-w-3xl">
      {showNameModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="coach-name-modal">
          <div className="bg-white rounded-[var(--kc-radius-md)] p-7 w-full max-w-md">
            <div className="font-display uppercase text-2xl">Name your coach</div>
            <p className="mt-2 text-muted-kc text-sm">What do you want to call your AI coach? Pick a name that feels like yours. You can change it anytime.</p>
            <input autoFocus value={nameInput} onChange={(e) => setNameInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }} maxLength={40} placeholder="e.g., Mick, Coach J, Sage…" className="mt-4 w-full h-12 px-4 rounded-[var(--kc-radius-sm)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid="coach-name-input"/>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => { setShowNameModal(false); setCoachName('Mick'); api.post('/coach/profile', { coach_name: 'Mick' }); }} className="h-11 px-4 rounded-[var(--kc-radius-sm)] hover:bg-surface-2 transition-colors text-sm font-semibold" data-testid="coach-name-skip">Skip (use “Mick”)</button>
              <button onClick={saveName} className="h-11 px-5 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="coach-name-save">Save</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-muted-kc flex items-center gap-1"><MessageCircle size={14}/> AI Coach</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl mt-1">Ask <span className="text-gold">{coachName || 'Mick'}</span></h1>
          <p className="text-muted-kc text-sm mt-1 max-w-lg">Trained on HEADSPACE principles. Honest, no-fluff coaching.</p>
        </div>
        <div className="flex gap-1">
          <button onClick={() => { setNameInput(coachName || ''); setShowNameModal(true); }} className="h-9 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs text-muted-kc hover:bg-surface-2 transition-colors" data-testid="coach-rename" title="Rename coach"><Edit3 size={12}/></button>
          {messages.length > 0 && (
            <button onClick={clearHistory} className="h-9 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs text-muted-kc hover:bg-surface-2 transition-colors" data-testid="coach-clear"><RefreshCw size={12}/> Clear</button>
          )}
        </div>
      </div>

      {/* DISCLAIMER */}
      {showDisclaimer && (
        <div className="mb-4 p-4 bg-gold-soft border border-[color:var(--kc-gold)] rounded-[var(--kc-radius-md)] text-sm relative" data-testid="coach-disclaimer">
          <button onClick={dismissDisclaimer} className="absolute top-2 right-2 p-1 text-muted-kc" data-testid="coach-disclaimer-close"><X size={14}/></button>
          <div className="font-semibold uppercase tracking-wider text-[11px] flex items-center gap-1 mb-1"><AlertTriangle size={14} className="text-[color:var(--kc-gold-2)]"/> Important — please read</div>
          <p className="text-ink-2 leading-relaxed pr-6">
            {coachName || 'Mick'} is an AI coach trained on mindset principles — <strong>not a licensed therapist, doctor, or mental health professional</strong>. Conversations are for educational and motivational purposes only and are <strong>not a substitute for professional medical, psychological, or therapeutic advice, diagnosis, or treatment</strong>. By using this chat you agree that Kickass Course and Mickey Trivett are not liable for decisions you make based on its responses. <strong>If you are in crisis or experiencing thoughts of self-harm, please call or text 988 (US) or your local emergency number immediately.</strong>
          </p>
        </div>
      )}

      {/* CRISIS BANNER */}
      {crisisAlert && (
        <div className="mb-4 p-4 bg-ink text-white rounded-[var(--kc-radius-md)] border-l-4 border-[color:var(--kc-gold)] flex items-start gap-3" data-testid="crisis-banner">
          <Phone className="text-[color:var(--kc-gold)] mt-1 flex-shrink-0" size={20}/>
          <div>
            <div className="font-display uppercase text-lg">You are not alone.</div>
            <p className="text-white/85 text-sm mt-1">If you're in crisis or having thoughts of self-harm, please reach out right now. <a href="tel:988" className="underline font-semibold text-[color:var(--kc-gold)]">Call or text 988</a> — free, confidential, 24/7.</p>
          </div>
          <button onClick={() => setCrisisAlert(false)} className="ml-auto text-white/60" data-testid="crisis-banner-close"><X size={16}/></button>
        </div>
      )}

      <div ref={scrollRef} className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 md:p-6 h-[55vh] overflow-y-auto space-y-4" data-testid="coach-messages">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="font-display uppercase text-3xl text-ink">Take a seat.</div>
            <p className="text-muted-kc text-sm max-w-md mt-2">Tell {coachName || 'me'} what's on your mind. Or pick a starter below.</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2 max-w-xl">
              {QUICK_PROMPTS.map((q, i) => (
                <button key={i} onClick={() => send(q)} className="px-3 py-2 rounded-full bg-surface-2 border border-kc text-sm hover:border-gold hover:bg-gold-soft transition-colors" data-testid={`coach-quick-${i}`}>{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={m.id || i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role === 'user' ? (
              <div className="max-w-[80%] bg-ink text-white rounded-[18px] rounded-br-[6px] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid="coach-user-msg">{m.content}</div>
            ) : (
              <div className="max-w-[85%] bg-white border border-kc text-ink rounded-[18px] rounded-bl-[6px] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap border-l-2 border-l-[color:var(--kc-gold)]" data-testid="coach-assistant-msg">{m.content}</div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="max-w-[85%] bg-white border border-kc text-muted-kc rounded-[18px] rounded-bl-[6px] px-4 py-3 text-sm leading-relaxed border-l-2 border-l-[color:var(--kc-gold)] italic">{coachName || 'Coach'} is thinking…</div>
          </div>
        )}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2" data-testid="coach-form">
        <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={`What's on your mind?`} rows={2} className="flex-1 p-3 rounded-[var(--kc-radius-sm)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)] text-sm resize-none" data-testid="coach-input"/>
        <button type="submit" disabled={sending || !input.trim()} className="h-auto px-5 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors disabled:opacity-50" data-testid="coach-send"><Send size={18}/></button>
      </form>

      <p className="mt-3 text-[11px] text-muted-kc text-center">Educational guidance only — not a substitute for professional advice. In crisis? <a href="tel:988" className="text-ink font-semibold underline">Call/text 988</a>.</p>
    </div>
  );
}
